import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { translateKey } from 'src/locales/translate';
import {
  CreateReminderDtoCustomRecurrenceUnitEnum,
  CreateReminderDtoCustomRecurrenceWeekOfMonthEnum,
} from 'woofwoof-api';
import { SPAN_WIDE } from './medicationFormLayout';

const CUSTOM_UNIT_OPTIONS = [
  { value: CreateReminderDtoCustomRecurrenceUnitEnum.Hour, label: 'godziny' },
  { value: CreateReminderDtoCustomRecurrenceUnitEnum.Day, label: 'dni' },
  { value: CreateReminderDtoCustomRecurrenceUnitEnum.Week, label: 'tygodnie' },
  {
    value: CreateReminderDtoCustomRecurrenceUnitEnum.Month,
    label: translateKey('hardcoded.miesiace'),
  },
];

const DAY_OF_WEEK_OPTIONS = [
  { value: 0, label: translateKey('calendar.weekdayLongSun') },
  { value: 1, label: translateKey('calendar.weekdayLongMon') },
  { value: 2, label: translateKey('calendar.weekdayLongTue') },
  { value: 3, label: translateKey('calendar.weekdayLongWed') },
  { value: 4, label: translateKey('calendar.weekdayLongThu') },
  { value: 5, label: translateKey('calendar.weekdayLongFri') },
  { value: 6, label: translateKey('calendar.weekdayLongSat') },
];

const WEEK_OF_MONTH_OPTIONS = [
  {
    value: CreateReminderDtoCustomRecurrenceWeekOfMonthEnum.First,
    label: 'Pierwszy',
  },
  {
    value: CreateReminderDtoCustomRecurrenceWeekOfMonthEnum.Second,
    label: 'Drugi',
  },
  {
    value: CreateReminderDtoCustomRecurrenceWeekOfMonthEnum.Third,
    label: 'Trzeci',
  },
  {
    value: CreateReminderDtoCustomRecurrenceWeekOfMonthEnum.Fourth,
    label: 'Czwarty',
  },
  {
    value: CreateReminderDtoCustomRecurrenceWeekOfMonthEnum.Last,
    label: 'Ostatni',
  },
];

interface MedicationReminderRecurrenceProps {
  disabled: boolean;
}

/** The "co N dni / tygodni" part of a reminder, shown only for a custom one. */
export default function MedicationReminderRecurrence({
  disabled,
}: MedicationReminderRecurrenceProps) {
  const { control, watch } = useFormContext();
  const reminderCustomUnit = watch('reminderCustomUnit');

  return (
    <Box
      sx={{
        ...SPAN_WIDE,
        display: 'grid',
        gridTemplateColumns: { xs: '1fr 1fr', md: '80px 1fr 1fr' },
        gap: 2,
      }}
    >
      <Controller
        name="reminderCustomEvery"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            label="Co"
            type="number"
            disabled={disabled}
            inputProps={{ min: 1 }}
            onChange={(e) => field.onChange(Number(e.target.value))}
          />
        )}
      />

      <FormControl fullWidth>
        <InputLabel shrink id="reminder-unit-label">
          Jednostka
        </InputLabel>
        <Controller
          name="reminderCustomUnit"
          control={control}
          render={({ field }) => (
            <Select
              {...field}
              labelId="reminder-unit-label"
              label="Jednostka"
              notched
              disabled={disabled}
            >
              {CUSTOM_UNIT_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          )}
        />
      </FormControl>

      {(reminderCustomUnit === CreateReminderDtoCustomRecurrenceUnitEnum.Week ||
        reminderCustomUnit ===
          CreateReminderDtoCustomRecurrenceUnitEnum.Month) && (
        <FormControl fullWidth>
          <InputLabel shrink id="reminder-dow-label">
            {translateKey('hardcoded.dzien_tygodnia')}
          </InputLabel>
          <Controller
            name="reminderCustomDayOfWeek"
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                labelId="reminder-dow-label"
                label={translateKey('hardcoded.dzien_tygodnia')}
                notched
                disabled={disabled}
              >
                <MenuItem value="">Dowolny</MenuItem>
                {DAY_OF_WEEK_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            )}
          />
        </FormControl>
      )}

      {reminderCustomUnit ===
        CreateReminderDtoCustomRecurrenceUnitEnum.Month && (
        <FormControl fullWidth>
          <InputLabel shrink id="reminder-wom-label">
            {translateKey('hardcoded.tydzien_miesiaca')}
          </InputLabel>
          <Controller
            name="reminderCustomWeekOfMonth"
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                labelId="reminder-wom-label"
                label={translateKey('hardcoded.tydzien_miesiaca')}
                notched
                disabled={disabled}
              >
                <MenuItem value="">Dowolny</MenuItem>
                {WEEK_OF_MONTH_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            )}
          />
        </FormControl>
      )}
    </Box>
  );
}
